/**
 * LocalStorage Authentication Provider
 * Current implementation using localStorage
 * Will be replaced with CookieAuthProvider when API supports it
 */

import type {
  AuthStorageProvider,
  AuthTokens,
  AuthUser,
} from "../auth.interface";

export class LocalStorageAuthProvider implements AuthStorageProvider {
  private readonly TOKEN_KEY = "authToken";
  private readonly REFRESH_TOKEN_KEY = "refreshToken";
  private readonly TOKEN_EXPIRY_KEY = "tokenExpiration";
  private readonly REFRESH_EXPIRY_KEY = "refreshTokenExpiration";
  private readonly USER_DATA_KEY = "userData";
  private readonly REMEMBER_ME_KEY = "rememberMe";

  async setTokens(tokens: AuthTokens): Promise<void> {
    try {
      localStorage.setItem(this.TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(this.TOKEN_EXPIRY_KEY, tokens.expiresAt.toString());

      if (tokens.refreshToken) {
        localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refreshToken);
      }

      if (tokens.refreshExpiresAt) {
        localStorage.setItem(
          this.REFRESH_EXPIRY_KEY,
          tokens.refreshExpiresAt.toString(),
        );
      }
    } catch {
      // Handle quota exceeded or other storage errors
      throw new Error("Failed to store authentication tokens");
    }
  }

  async getTokens(): Promise<AuthTokens | null> {
    const accessToken = localStorage.getItem(this.TOKEN_KEY);
    const expiresAt = localStorage.getItem(this.TOKEN_EXPIRY_KEY);

    if (!accessToken || !expiresAt) {
      return null;
    }

    const tokens: AuthTokens = {
      accessToken,
      expiresAt: parseInt(expiresAt, 10),
    };

    const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    const refreshExpiresAt = localStorage.getItem(this.REFRESH_EXPIRY_KEY);

    if (refreshToken) {
      tokens.refreshToken = refreshToken;
    }

    if (refreshExpiresAt) {
      tokens.refreshExpiresAt = parseInt(refreshExpiresAt, 10);
    }

    return tokens;
  }

  async clearTokens(): Promise<void> {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    localStorage.removeItem(this.REFRESH_EXPIRY_KEY);
  }

  async setUser(user: AuthUser): Promise<void> {
    try {
      localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(user));
    } catch {
      throw new Error("Failed to store user data");
    }
  }

  async getUser(): Promise<AuthUser | null> {
    const userData = localStorage.getItem(this.USER_DATA_KEY);
    if (!userData) {
      return null;
    }

    try {
      return JSON.parse(userData);
    } catch {
      // Corrupted data, clear it
      await this.clearUser();
      return null;
    }
  }

  async clearUser(): Promise<void> {
    localStorage.removeItem(this.USER_DATA_KEY);
  }

  async hasAuth(): Promise<boolean> {
    const tokens = await this.getTokens();
    const user = await this.getUser();
    return !!(tokens && user);
  }

  async setRememberMe(value: boolean): Promise<void> {
    localStorage.setItem(this.REMEMBER_ME_KEY, value.toString());
  }

  async getRememberMe(): Promise<boolean> {
    const value = localStorage.getItem(this.REMEMBER_ME_KEY);
    return value === "true";
  }
}
